import DeleteAccountButton from '../components/DeleteAccountButton';
import React, { useState, useEffect, useRef } from 'react';
import ConversationActions from '../components/ConversationActions';
import { getPartnerTheme } from '../config/partnerThemes';
import VoiceChoiceModal from '../components/dashboard/modals/VoiceChoiceModal';
import { prepareCoachVoice, loadCoachVoices, saveCoachVoice, previewCoachVoice, playCoachVoice, stopCoachVoice, coachVoiceActive } from '../utils/coachVoice';
import { Target, MessageCircle, BookOpen, Send, Zap, Wind, Eye, BarChart3, Share2 } from 'lucide-react';
import ResponseCounter from '../components/auth/ResponseCounter';
import PaywallModal from '../components/auth/PaywallModal';
import UserGuideModal from '../components/dashboard/modals/UserGuideModal';
import BibleReferenceModal from '../components/dashboard/modals/BibleReferenceModal';
import BiblicalTopicsModal from '../components/dashboard/modals/BiblicalTopicsModal';
import ChangePasswordModal from '../components/dashboard/modals/ChangePasswordModal';
import ShareModal from '../components/dashboard/modals/ShareModal';
import PracticeDetailModal from '../components/dashboard/modals/PracticeDetailModal';
import ProgressTab from '../components/dashboard/tabs/ProgressTab';
import AnalyticsTab from '../components/dashboard/tabs/AnalyticsTab';
import conversationService from '../services/conversations';
import ChatTab from '../components/dashboard/tabs/ChatTab';
import PracticeTab from '../components/dashboard/tabs/PracticeTab';

// ============================================================================
// CONSCIOUSNESS LEVELS CONFIGURATION
// ============================================================================
const LEVELS = [
  { level: 1, name: "SEEKING", min: 0, max: 120, color: "#4A5568", badge: "Truth Seeker" },
  { level: 2, name: "UNDERSTANDING", min: 121, max: 300, color: "#4A5568", badge: "Power Seeker" },
  { level: 3, name: "EMBRACING", min: 301, max: 600, color: "#CD7F32", badge: "Love-Spectrum Activator" },
  { level: 4, name: "DEMONSTRATING", min: 601, max: 1000, color: "#FFD700", badge: "Clarity Bringer" },
  { level: 5, name: "SIMINATING", min: 1001, max: 1500, color: "#50C878", badge: "Fho Practitioner" },
  { level: 6, name: "TRANSFORMING", min: 1501, max: 2000, color: "#50C878", badge: "Energy Transformer" },
  { level: 7, name: "ASCENDING", min: 2001, max: 2700, color: "#0066FF", badge: "Swamp Climber" },
  { level: 8, name: "TRANSCENDING", min: 2701, max: 3500, color: "#0066FF", badge: "3d Swamp Escapee" },
  { level: 9, name: "ILLUMINATING", min: 3501, max: 4500, color: "#9966CC", badge: "I-WE Harmonizer" },
  { level: 10, name: "EMBODYING", min: 4501, max: 5800, color: "#9966CC", badge: "Free Happy One" },
  { level: 11, name: "RADIATING", min: 5801, max: 7500, color: "#B9F2FF", badge: "Spirit Made Physical" },
  { level: 12, name: "DIVINE STASIS", min: 7501, max: 999999, color: "#FFD700", badge: "Divine Stasis Master" }
];

function getLevelInfo(points) {
  const currentLevel = LEVELS.find(l => points >= l.min && points <= l.max) || LEVELS[0];
  const nextLevel = LEVELS[currentLevel.level] || currentLevel;
  const progressInLevel = points - currentLevel.min;
  const levelRange = currentLevel.max - currentLevel.min;
  const percentComplete = Math.min(100, (progressInLevel / levelRange) * 100);
  const pointsToNext = nextLevel.min - points;
  return {
    currentLevel, nextLevel,
    percentComplete: Math.round(percentComplete),
    pointsToNext: Math.max(0, pointsToNext),
    isMaxLevel: currentLevel.level === 12
  };
}

function getNextLevelUnlock(level) {
  const unlocks = {
    2: "Power MindStream + Imagination & Reasoning tools",
    3: "Flow MindStream + Intuition & Perception tools",
    4: "Clarity MindStream + All 36 Dimensions accessible",
    5: "Simination Score + FAME tracking + All tools active",
    6: "THAT Energy System + Freedom Score tracking",
    7: "3d Swamp Escape metrics + Rising from dependency",
    8: "Full Transcendence",
    9: "Fholific Leadership + Teaching capabilities",
    10: "Free Happy One status + Perfect Simination",
    11: "Spirit Made Physical + Legacy creation",
    12: "Divine Stasis Master + Co-creator status"
  };
  return unlocks[level] || "Advanced features";
}

const MinimalDashboard = () => {
  const apiUrl = window.location.origin;
  const [activeTab, setActiveTab] = useState('chat');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [emitConversations, setEmitConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [fhoAppsOpen, setFhoAppsOpen] = useState(false);
  const [activeCoach, setActiveCoach] = useState('emit');
  const [dxi, setDxi] = useState(null);
  const [dxiPulse, setDxiPulse] = useState(null);
  const [dxiReward, setDxiReward] = useState(null);
  const menuRef = React.useRef(null);

  // Ambient DXI chip. Refetches after a response and pulses the stream that
  // moved. Compares against the previous domain scores rather than reading
  // the SSE frame, because the journey write is fire-and-forget and may not
  // have landed when the stream closes.
  const refreshDxi = React.useCallback(async (pulse = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch('/api/v1/journey/dxi', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const next = await res.json();
      setDxi(prev => {
        if (pulse && prev && next && prev.domains && next.domains) {
          const streamOf = { D: 'Power', OXY: 'Flow', SE: 'Flow', REN: 'Clarity' };
          // Compare EXACT values — a session moves a domain by ~0.025, which
          // is invisible once rounded. Only an increase fires; never a
          // decrease, never first load.
          const deltas = ['D', 'OXY', 'SE', 'REN']
            .map(k => ({
              k,
              stream: streamOf[k],
              d: (next.domains[k]?.exact ?? next.domains[k]?.score ?? 0)
                 - (prev.domains[k]?.exact ?? prev.domains[k]?.score ?? 0),
            }))
            .filter(x => x.d > 0.001)
            .sort((a, b) => b.d - a.d);
          if (deltas.length) {
            const top = deltas[0];
            setDxiPulse(top.stream);
            setTimeout(() => setDxiPulse(null), 2200);
            setDxiReward({
              stream: top.stream,
              amount: top.d < 0.1 ? top.d.toFixed(2) : top.d.toFixed(1),
              id: Date.now(),
            });
            setTimeout(() => setDxiReward(null), 2400);
          }
        }
        return next;
      });
    } catch (e) {
      // The chip is ambient. A failure here must never surface.
    }
  }, []);

  useEffect(() => { refreshDxi(false); }, [refreshDxi]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const loadEmitHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/emit/conversations', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmitConversations(data.conversations || []);
      }
    } catch (error) { console.error('Error loading history:', error); }
    finally { setLoadingHistory(false); }
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/emit/conversations/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations([{ id: conversationId, messages: data.messages.map(msg => ({ role: msg.role, content: msg.content })) }]);
        setCurrentConversationId(conversationId);
        setShowConversationHistory(false);
      }
    } catch (error) { console.error('Error loading conversation:', error); }
  };

  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatError, setChatError] = useState('');
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [queryCategory, setQueryCategory] = useState('biblical');
  const [practiceForm, setPracticeForm] = useState({ mindstream_type: 'power', practice_type: '', practice_name: '', duration_minutes: 30, quality_rating: 8, insights_gained: '' });
  const [isSubmittingPractice, setIsSubmittingPractice] = useState(false);
  const [practiceSuccess, setPracticeSuccess] = useState('');
  const [practiceError, setPracticeError] = useState('');
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [responsesRemaining, setResponsesRemaining] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [showBibleReference, setShowBibleReference] = useState(false);
  const [showBiblicalTopics, setShowBiblicalTopics] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  // Voice choice (apps whose theme sets voices: the Chaplain app)
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  const [voiceList, setVoiceList] = useState([]);
  const [voiceChoice, setVoiceChoice] = useState(null);
  // EMIT_VOICE_V1: accounts on the Research/Sermon list also get the voice choice in Emit
  const emitVoiceOk = (() => { try { return ['thornton.carlton68@gmail.com', 'thornton_carlton@yahoo.com', 'hbaker@agencyofamerica.com', 'cjrealfellas@gmail.com', 'kbarthor1@aol.com'].includes(JSON.parse(localStorage.getItem('user') || '{}').email); } catch (e) { return false; } })();
  useEffect(() => {
    if (!(getPartnerTheme().voices || emitVoiceOk)) return;
    prepareCoachVoice();
    loadCoachVoices().then((d) => { if (d) { setVoiceList(d.voices); setVoiceChoice(d.choice); } });
  }, []);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const messagesEndRef = useRef(null);
  const latestMessageRef = useRef(null);

  const useVoiceChat = () => {
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(typeof window !== 'undefined' && localStorage.getItem('emitVoiceEnabled') === 'true');
    const recognitionRef = useRef(null);
    const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);
    const keepAliveRef = useRef(null);

    useEffect(() => {
      if (typeof window === 'undefined') return;
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        recognitionRef.current = new SR();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-US';
        recognitionRef.current.onresult = (event) => {
          setTranscript(event.results[event.resultIndex][0].transcript);
        };
        recognitionRef.current.onerror = () => setIsListening(false);
        recognitionRef.current.onend = () => setIsListening(false);
      }
      return () => {
        if (recognitionRef.current) recognitionRef.current.stop();
        if (synthRef.current) synthRef.current.cancel();
      };
    }, []);

    const startListening = () => { if (recognitionRef.current && !isListening) { setTranscript(''); recognitionRef.current.start(); setIsListening(true); } };
    const stopListening = () => { if (recognitionRef.current && isListening) { recognitionRef.current.stop(); setIsListening(false); } };
    const stripMarkdownForVoice = (text) => text.replace(/\[([^\]]+)\]\([^)]+\)/g,'$1').replace(/\*\*([^*]+)\*\*/g,'$1').replace(/\*([^*]+)\*/g,'$1').replace(/__([^_]+)__/g,'$1').replace(/_([^_]+)_/g,'$1').replace(/FHO-OS/gi,'foe OS').replace(/FHO/gi,'foe');
    const speak = (text) => {
      if (!voiceEnabled) return;
      if (coachVoiceActive()) {
        if (synthRef.current) synthRef.current.cancel();
        playCoachVoice(text, () => setIsSpeaking(true), () => setIsSpeaking(false), (rest) => speakDevice(rest));
        return;
      }
      speakDevice(text);
    };
    const speakDevice = (text) => {
      if (!voiceEnabled || !synthRef.current) return;
      const synth = synthRef.current;
      const clean = stripMarkdownForVoice(text);
      if (!clean || !clean.trim()) { console.warn('[tts] nothing to speak'); return; }

      const go = () => {
        const utterance = new SpeechSynthesisUtterance(clean);
        utterance.rate = 0.90; utterance.pitch = 0.85; utterance.volume = 1.0;
        const voices = synth.getVoices();
        const preferredVoice = voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('male'));
        if (preferredVoice) utterance.voice = preferredVoice;

        // Chrome stops speaking after ~15s unless the engine is nudged.
        if (keepAliveRef.current) clearInterval(keepAliveRef.current);
        keepAliveRef.current = setInterval(() => {
          if (synth.speaking) { synth.pause(); synth.resume(); }
          else { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
        }, 10000);

        const clearKeepAlive = () => {
          if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
        };

        utterance.onstart = () => { setIsSpeaking(true); console.log('[tts] started', clean.length, 'chars'); };
        utterance.onend = () => { setIsSpeaking(false); clearKeepAlive(); };
        utterance.onerror = (e) => {
          setIsSpeaking(false); clearKeepAlive();
          console.error('[tts] error:', e.error, '| voices:', synth.getVoices().length, '| chars:', clean.length);
        };

        synth.speak(utterance);

        // If nothing started within a second, say so rather than failing silently.
        setTimeout(() => {
          if (!synth.speaking && !synth.pending) {
            console.warn('[tts] speak() produced no audio \u2014 voices:', synth.getVoices().length);
          }
        }, 1000);
      };

      // cancel() is async internally; giving speak() its own tick avoids a swallowed call
      synth.cancel();
      if (synth.getVoices().length === 0) {
        const onVoices = () => { synth.removeEventListener('voiceschanged', onVoices); setTimeout(go, 60); };
        synth.addEventListener('voiceschanged', onVoices);
        setTimeout(() => { synth.removeEventListener('voiceschanged', onVoices); go(); }, 700);
      } else {
        setTimeout(go, 60);
      }
    };

    const stopSpeaking = () => { stopCoachVoice(); if (synthRef.current) synthRef.current.cancel(); setIsSpeaking(false); };
    const toggleVoice = () => {
      const newState = !voiceEnabled;
      setVoiceEnabled(newState);
      if (typeof window !== 'undefined') localStorage.setItem('emitVoiceEnabled', newState);
      if (!newState) stopSpeaking();
    };
    const setVoiceOn = (on) => {
      setVoiceEnabled(on);
      if (typeof window !== 'undefined') localStorage.setItem('emitVoiceEnabled', on);
      if (!on) stopSpeaking();
    };
    return { isListening, isSpeaking, transcript, voiceEnabled, startListening, stopListening, speak, stopSpeaking, toggleVoice, setVoiceOn, isSupported: !!recognitionRef.current };
  };

  const voice = useVoiceChat();
  const lastSpokenMessageRef = useRef(null);

  const categories = [
    { value: 'biblical', label: '📖 Biblical Study', icon: '📖', description: 'Scripture analysis through Fhoism lens (9-section format)' },
    { value: 'general', label: '💬 General Guidance', icon: '💬', description: 'Open dialogue about any topic' },
    { value: 'self-development', label: '🌱 Self-Development', icon: '🌱', description: 'Personal growth & transformation steps' },
    { value: 'mindstreams', label: '🧠 MindStreams', icon: '🧠', description: 'Power, Flow & Clarity practices' }
  ];

  useEffect(() => { fetchDashboardData(); loadConversations(); }, []);

  useEffect(() => {
    const checkEmitProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await fetch(`${apiUrl}/api/v1/profile/emit-profile`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (response.ok) {
          const data = await response.json();
          if (!data.exists || !data.preferred_name) window.location.href = '/emit-the-bible/setup';
          else {
            setUserName(data.preferred_name);
            // The coach chosen in setup: standard Emit and the Chaplain app (branded Christian partners stay on Emit).
            const _t = getPartnerTheme();
            if (data.coach_preference === 'sage' && (!_t.partnerName || _t.id === 'chaplain' || _t.id === 'faith')) setActiveCoach('sage');
          }
        }
      } catch (err) { console.log('Profile check error:', err); }
    };
    checkEmitProfile();
  }, []);

  useEffect(() => { if (voice.transcript) setUserInput(voice.transcript); }, [voice.transcript]);
  // Auto-send when browser silence detection cuts mic
  const wasListeningRef = useRef(false);
  useEffect(() => {
    if (wasListeningRef.current && !voice.isListening && userInput.trim()) {
      setTimeout(() => handleSubmit(), 150);
    }
    wasListeningRef.current = voice.isListening;
  }, [voice.isListening]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promptParam = urlParams.get('prompt');
    const autoSend = urlParams.get('send') === 'true';
    if (promptParam) {
      const decodedPrompt = decodeURIComponent(promptParam);
      if (autoSend) localStorage.setItem('emit_pendingPrompt', decodedPrompt);
      setUserInput(decodedPrompt);
      window.history.replaceState({}, '', window.location.pathname);
      if (autoSend) setTimeout(() => { handleSubmit(decodedPrompt); }, 500);
    }
  }, []);

  useEffect(() => {
    const journeyPrompt = localStorage.getItem('journeyFocusPrompt');
    if (journeyPrompt) {
      localStorage.removeItem('journeyFocusPrompt');
      localStorage.removeItem('journeyFocusCategory');
      setUserInput(journeyPrompt);
      setTimeout(() => { handleSubmit(journeyPrompt); }, 500);
    }
  }, []);

  useEffect(() => { if (activeTab === "practice") window.scrollTo({ top: 0, behavior: "smooth" }); }, [activeTab]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const storedUser = localStorage.getItem('user');
      const userId = storedUser ? JSON.parse(storedUser).id : 'test_user_123';
      const response = await fetch(`${apiUrl}/fho/dashboard/${userId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSubscriptionStatus(data.subscription_status);
      if (data.subscription_status === 'free') setResponsesRemaining(data.responses_remaining);
      const transformedData = {
        profile: { username: data.profile?.username || 'User', email: data.profile?.email || 'user@emit.com', consciousness_points: data.profile?.consciousness_points || ((data.mindstreams || []).reduce((sum, s) => sum + (s.sessions_completed || 0), 0) * 28) },
        ecc_alert: data.ecc_alert || null,
        current_stage: { stage_number: data.profile?.consciousness_level || 1, stage_name: data.profile?.journey_stage || 'Beginning Journey' },
        progress: { total_practice_sessions: data.profile?.total_practices || (data.mindstreams || []).reduce((sum, s) => sum + (s.sessions_completed || 0), 0), days_active: data.daily_stats?.total_days_active || 0, streak_days: data.profile?.current_streak || Math.max(...(data.mindstreams || []).map(s => s.current_streak || 0), 0) },
        mindstreams: (data.mindstreams || []).map(stream => ({ name: stream.stream_type === 'power' ? 'Power' : stream.stream_type === 'flow' ? 'Flow' : 'Clarity', type: stream.stream_type, level: stream.level, score: Math.round(stream.progress_percentage || 0), sessions: stream.sessions_completed || 0, time_minutes: stream.total_time_minutes || 0, streak: stream.current_streak || 0 })),
        dimensions: (data.das_abilities || []).map(ability => ({ name: ability.ability_name, power: ability.power_name, mindstream: ability.das_type, score: Math.round(ability.mastery_percentage || 0), times_practiced: ability.times_practiced || 0, is_active: ability.is_active })),
        recent_practices: (data.recent_practices || []).length > 0
          ? data.recent_practices.map(practice => ({ practice_type: practice.practice_name, notes: practice.insights_gained || 'No notes', duration_minutes: practice.duration_minutes, impact_score: practice.quality_rating, practice_date: practice.practiced_at, mindstream_type: practice.mindstream_type }))
          : data.mindstreams.filter(s => s.last_session).map(stream => ({ practice_type: `${stream.stream_type.charAt(0).toUpperCase() + stream.stream_type.slice(1)} Practice`, notes: `${stream.sessions_completed} sessions completed`, duration_minutes: Math.round(stream.total_time_minutes / Math.max(stream.sessions_completed, 1)), impact_score: Math.round(stream.average_session_quality || 0), practice_date: stream.last_session, mindstream_type: stream.stream_type }))
      };
      setUserData(transformedData);
      setError(null);
    } catch (err) { console.error('Error fetching dashboard data:', err); setError('Failed to load dashboard data'); }
    finally { setLoading(false); }
  };

  const loadConversations = async () => {
    try {
      const loadedConversations = await conversationService.getConversations();
      setConversations(Array.isArray(loadedConversations) ? loadedConversations : []);
    } catch (err) { console.error('Error loading conversations:', err); setConversations([]); }
  };

  const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const generateTitleFromMessage = (message) => { const words = message.split(' ').slice(0, 5).join(' '); return words.length < message.length ? `${words}...` : words; };
  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };
  const scrollToLatestMessage = () => { latestMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } };
  const getMindStreamIcon = (type) => {
    switch(type) {
      case 'power': return <Zap className="mindstream-icon" style={{ color: '#0d6efd' }} />;
      case 'flow': return <Wind className="mindstream-icon" style={{ color: '#dc3545' }} />;
      case 'clarity': return <Eye className="mindstream-icon" style={{ color: '#198754' }} />;
      default: return <Target className="mindstream-icon" />;
    }
  };
  const getMindStreamColor = (type) => { switch(type) { case 'power': return '#0d6efd'; case 'flow': return '#dc3545'; case 'clarity': return '#198754'; default: return '#4A90E2'; } };

  const handleTopicSelect = (topicPrompt) => { setUserInput(topicPrompt); handleSubmit(topicPrompt); };

  const handleSubmit = async (directPrompt = null) => {
    const messageToSend = directPrompt || userInput;
    if (isProcessing || !messageToSend.trim()) { setChatError('Please enter a question or topic'); return; }
    setIsProcessing(true); setChatError('');
    let activeConvId = currentConversationId;
    if (!activeConvId) {
      const newConv = { id: generateId(), title: generateTitleFromMessage(messageToSend), messages: [], category: queryCategory, createdAt: Date.now(), updatedAt: Date.now() };
      setConversations(prev => [...prev, newConv]);
      setCurrentConversationId(newConv.id);
      activeConvId = newConv.id;
    }
    const userMessage = { id: generateId(), role: 'user', content: messageToSend, category: queryCategory, timestamp: Date.now() };
    try {
      setConversations(prev => prev.map(conv => conv.id === activeConvId ? { ...conv, messages: [...conv.messages, userMessage], updatedAt: Date.now() } : conv));
      setUserInput('');
      setTimeout(scrollToBottom, 100);
      const currentConv = Array.isArray(conversations) ? conversations.find(c => c.id === activeConvId) : null;
      const messageHistory = currentConv ? [...currentConv.messages, userMessage].slice(-10) : [userMessage];
      const response = await fetch(`${apiUrl}/api/v1/claude-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: userMessage.content, category: queryCategory, conversation_id: activeConvId, message_history: messageHistory, user_email: JSON.parse(localStorage.getItem("user"))?.email, coach: activeCoach, partner: getPartnerTheme().id, stream: true })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const assistantMessageId = generateId();
      let accumulatedText = '';
      setConversations(prev => prev.map(conv => conv.id === activeConvId ? { ...conv, messages: [...conv.messages, { id: assistantMessageId, role: 'assistant', content: '', category: queryCategory, timestamp: Date.now(), isStreaming: true }], updatedAt: Date.now() } : conv));
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value).split('\n')) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.error) throw new Error(data.error);
              if (data.chunk) {
                accumulatedText += data.chunk;
                setConversations(prev => prev.map(conv => conv.id === activeConvId ? { ...conv, messages: conv.messages.map(msg => msg.id === assistantMessageId ? { ...msg, content: accumulatedText, isStreaming: !data.done } : msg), updatedAt: Date.now() } : conv));
                setTimeout(scrollToLatestMessage, 10);
              }
              if (data.done) {
                if (data.responses_remaining !== undefined) { setResponsesRemaining(data.responses_remaining); if (data.responses_remaining === 0) setTimeout(() => setShowPaywall(true), 2000); }
                setConversations(prev => prev.map(conv => conv.id === activeConvId ? { ...conv, messages: conv.messages.map(msg => msg.id === assistantMessageId ? { ...msg, isStreaming: false } : msg), updatedAt: Date.now() } : conv));
                setConversations(prev => { const updatedConv = prev.find(c => c.id === activeConvId); if (updatedConv) conversationService.updateConversation(activeConvId, { title: updatedConv.title, messages: updatedConv.messages, updated_at: new Date(updatedConv.updatedAt).toISOString() }).catch(err => console.warn('Save failed:', err.message)); return prev; });
                if (voice.voiceEnabled && accumulatedText) setTimeout(() => { voice.speak(accumulatedText); lastSpokenMessageRef.current = assistantMessageId; }, 500);
                setTimeout(scrollToLatestMessage, 100);
                // 1.2s: the journey write is a fire-and-forget task.
                setTimeout(() => refreshDxi(true), 1200);
              }
            } catch (parseError) { console.error('Error parsing SSE data:', parseError); }
          }
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const status = err?.response?.status || err?.status;
      const text = (err?.message || '') + ' ' + (err?.response?.data?.detail || '');
      const isOutage = [500, 502, 503, 504, 529].includes(status) ||
        /\b(500|502|503|504|529|overloaded|unavailable|api error)\b/i.test(text);
      setChatError(isOutage
        ? "⚠️ The AI is temporarily unavailable right now (high demand on the service). Your message wasn't lost — please try again in a moment."
        : 'Failed to send message. Please try again.');
    }
    finally { setIsProcessing(false); }
  };

  const handlePracticeSubmit = async (e) => {
    e.preventDefault();
    if (!practiceForm.practice_name.trim()) { setPracticeError('Please enter a practice name'); return; }
    setIsSubmittingPractice(true); setPracticeError(''); setPracticeSuccess('');
    try {
      const storedUser = localStorage.getItem('user');
      const userId = storedUser ? JSON.parse(storedUser).id : 'test_user_123';
      const response = await fetch(`${apiUrl}/fho/practices/${userId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mindstream_type: practiceForm.mindstream_type, practice_type: practiceForm.practice_type, practice_name: practiceForm.practice_name, duration_minutes: parseInt(practiceForm.duration_minutes), quality_rating: parseInt(practiceForm.quality_rating), insights_gained: practiceForm.insights_gained }) });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data) { setPracticeSuccess('Practice logged successfully! 🎉'); await fetchDashboardData(); setPracticeForm({ mindstream_type: 'power', practice_type: '', practice_name: '', duration_minutes: 30, quality_rating: 8, insights_gained: '' }); await fetchDashboardData(); setTimeout(() => setPracticeSuccess(''), 3000); }
    } catch (err) { console.error('Error logging practice:', err); setPracticeError('Failed to log practice. Please try again.'); }
    finally { setIsSubmittingPractice(false); }
  };

  const handlePracticeClick = (practice) => { setSelectedPractice(practice); setShowPracticeModal(true); };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setPasswordError(''); setPasswordSuccess('');
    if (passwordForm.newPassword.length < 8) { setPasswordError('New password must be at least 8 characters'); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setPasswordError('New passwords do not match'); return; }
    setIsChangingPassword(true);
    try {
      const storedUser = localStorage.getItem('user');
      const userId = storedUser ? JSON.parse(storedUser).id : 'test_user_123';
      const response = await fetch(`${apiUrl}/api/v1/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userId, current_password: passwordForm.currentPassword, new_password: passwordForm.newPassword }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to change password');
      setPasswordSuccess('Password changed successfully! 🎉');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowChangePassword(false); setPasswordSuccess(''); }, 2000);
    } catch (error) { console.error('Error changing password:', error); setPasswordError(error.message || 'Failed to change password. Please try again.'); }
    finally { setIsChangingPassword(false); }
  };

  // ── Shared style for dropdown menu buttons ──────────────────────────────
  // Reduced from 10px 16px → 7px 12px so all items fit without scrolling
  const menuBtnStyle = {
    width: '100%',
    padding: '7px 12px',
    background: 'white',
    border: 'none',
    borderBottom: '1px solid #f0f0f0',
    color: '#2C3E50',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.2s ease'
  };

  const partnerTheme = getPartnerTheme();
  return (
    <div className={`emit-minimal-container${partnerTheme.partnerName ? " partner-mode" : ""}`} style={partnerTheme.partnerName ? { '--partner-primary': partnerTheme.colors.primary, '--partner-accent': partnerTheme.colors.accent, '--partner-bg': partnerTheme.colors.pageBg, '--partner-bubble': partnerTheme.colors.userBubble || partnerTheme.colors.primary } : undefined}>
      {/* Header */}
      <div className="emit-header" style={partnerTheme.partnerName ? { background: partnerTheme.colors.primary, position: 'relative' } : undefined}>
        <div style={{ position: 'absolute', top: partnerTheme.partnerName ? '14px' : '8px', left: partnerTheme.partnerName ? '16px' : '8px', display: 'flex', flexDirection: 'column', alignItems: partnerTheme.partnerName ? 'flex-start' : 'center', gap: '2px', zIndex: 1 }}>
          {partnerTheme.partnerName ? (
            partnerTheme.logo ? (
              <div style={partnerTheme.logoPlate ? { background: '#ffffff', borderRadius: '6px', padding: '7px 10px', display: 'inline-block', lineHeight: 0 } : undefined}>
                <img src={partnerTheme.logo} alt={partnerTheme.partnerName}
                  style={{ width: (partnerTheme.logoWidth || 200) * (window.innerWidth <= 768 ? 0.72 : 1), height: 'auto', display: 'block' }} />
              </div>
            ) : (
            <span style={{ fontSize: window.innerWidth <= 768 ? '15px' : '19px', color: '#fff', fontWeight: '800', letterSpacing: '0.3px', textAlign: 'left', lineHeight: 1.1 }}>
              {partnerTheme.partnerName}
              {partnerTheme.partnerSub && (
                <span style={{ display: 'block', fontSize: window.innerWidth <= 768 ? '8px' : '10px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: partnerTheme.colors.accent, marginTop: '3px' }}>{partnerTheme.partnerSub}</span>
              )}
            </span>
            )
          ) : (
            <>
              <span style={{ fontSize: window.innerWidth <= 768 ? '8px' : '10px', color: '#FFD700', fontWeight: '600', fontStyle: 'italic', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Powered by</span>
              <img src="/fho-logo.svg" alt="FHO" style={{ width: window.innerWidth <= 768 ? '70px' : '100px', height: window.innerWidth <= 768 ? '70px' : '100px', opacity: 0.9 }} />
            </>
          )}
        </div>
        {!partnerTheme.partnerName && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', background: 'rgba(0,0,0,0.25)', borderRadius: '32px', padding: '4px' }}>
            <button onClick={() => setActiveCoach('emit')} style={{ padding: window.innerWidth <= 768 ? '10px 22px' : '12px 36px', borderRadius: '32px', border: 'none', cursor: 'pointer', background: activeCoach === 'emit' ? 'linear-gradient(135deg, #B8860B, #FFD700)' : 'transparent', color: activeCoach === 'emit' ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: '800', fontSize: window.innerWidth <= 768 ? '18px' : '24px', transition: 'all 0.3s ease', letterSpacing: '1px', boxShadow: activeCoach === 'emit' ? '0 4px 15px rgba(255,215,0,0.4)' : 'none' }}>✨ Emit</button>
            <button onClick={() => setActiveCoach('sage')} style={{ padding: window.innerWidth <= 768 ? '10px 22px' : '12px 36px', borderRadius: '32px', border: 'none', cursor: 'pointer', background: activeCoach === 'sage' ? 'linear-gradient(135deg, #2E7D32, #4CAF50)' : 'transparent', color: activeCoach === 'sage' ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: '800', fontSize: window.innerWidth <= 768 ? '18px' : '24px', transition: 'all 0.3s ease', letterSpacing: '1px', boxShadow: activeCoach === 'sage' ? '0 4px 15px rgba(76,175,80,0.4)' : 'none' }}>💫 SAGE</button>
          </div>
          <span style={{ fontSize: '15px', color: '#FFD700', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all 0.3s ease' }}>
            {activeCoach === 'emit' ? '✝ Christian-Based' : '☯ Spiritual-Based'}
          </span>
          {dxi && (
            <span
              onClick={() => { window.location.href = '/journey'; }}
              title="Doxyseren Index — your practice across the three MindStreams"
              style={{
                fontSize: '12px',
                letterSpacing: '0.08em',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '3px 10px',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.45)',
                background: 'rgba(255,255,255,0.18)',
                color: dxiPulse
                  ? (dxiPulse === 'Power' ? '#3730a3'
                    : dxiPulse === 'Flow' ? '#b91c1c' : '#065f46')
                  : '#ffffff',
                textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                transition: 'color 0.4s ease, background 0.4s ease',
                background: dxiPulse ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.18)',
                whiteSpace: 'nowrap',
              }}
            >
              {dxiPulse ? `${dxiPulse} \u2191` : `DXI ${dxi.composite}`}
            </span>
          )}
        </div>
        )}
        {partnerTheme.partnerName && partnerTheme.poweredByFHO && (
          <div style={{ position: 'absolute', top: '16px', right: '84px', textAlign: 'right', zIndex: 1 }}>
            <span style={{ display: 'block', fontSize: '9px', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.5px' }}>powered by</span>
            <span style={{ fontSize: '16px', fontWeight: 900, letterSpacing: '0.5px' }}>
              <span style={{ color: '#8b93f0' }}>F</span><span style={{ color: '#f87171' }}>H</span><span style={{ color: '#34d399' }}>O</span>
            </span>
          </div>
        )}
        <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
          <button onClick={() => setShowMenu(!showMenu)}
            style={{ padding: '10px 16px', background: partnerTheme.partnerName ? 'rgba(255,255,255,0.10)' : 'linear-gradient(135deg, #4A90E2, #6CB4EE)', border: 'none', borderRadius: '20px', color: 'white', fontSize: '20px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: partnerTheme.partnerName ? 'inset 0 0 0 1px rgba(255,255,255,0.22)' : '0 2px 8px rgba(74,144,226,0.3)' }}
            onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 4px 12px rgba(74,144,226,0.4)'; }}
            onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 8px rgba(74,144,226,0.3)'; }}>
            ☰
          </button>

          {showMenu && (
            <div ref={menuRef} style={{ position: 'absolute', top: '0', right: '40px', background: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', overflow: 'auto', minWidth: '200px', maxHeight: 'calc(100vh - 80px)', zIndex: 1000 }}>

              {!partnerTheme.home && (
              <button onClick={() => { setShowMenu(false); window.location.href = '/platform'; }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                🌐 Explore FHO Platform
              </button>
              )}
              <button onClick={() => { setShowMenu(false); setShowBibleReference(true); }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                📖 Bible Reference
              </button>
              <button onClick={() => { setShowMenu(false); setShowBiblicalTopics(true); }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                💡 Biblical Topics
              </button>
              <button onClick={() => { setShowMenu(false); window.location.href = '/emit-the-bible/setup'; }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                ✏️ Edit Profile
              </button>
              {(partnerTheme.voices || emitVoiceOk) && (
              <button onClick={() => { setShowMenu(false); setShowVoicePanel(true); }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                {'\uD83D\uDD0A'} Voice
              </button>
              )}
              <button onClick={() => { setShowMenu(false); loadEmitHistory(); setShowConversationHistory(true); }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                📜 Conversation History
              </button>
              <button onClick={() => { setShowMenu(false); setCurrentConversationId(null); setConversations([{ id: Date.now().toString(), messages: [] }]); }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                🔄 New Conversation
              </button>
              <button onClick={() => { setShowMenu(false); setShowChangePassword(true); }}
                style={menuBtnStyle} onMouseOver={(e) => e.target.style.background = '#f8f9fa'} onMouseOut={(e) => e.target.style.background = 'white'}>
                🔒 Change Password
              </button>
              {["thornton.carlton68@gmail.com", "thornton_carlton@yahoo.com", "hbaker@agencyofamerica.com", "cjrealfellas@gmail.com", "kbarthor1@aol.com"].includes(JSON.parse(localStorage.getItem('user') || '{"email":""}').email) && (
                <button onClick={() => { setShowMenu(false); window.location.href = '/emit-research'; }}
                  style={{ ...menuBtnStyle, background: 'rgba(79,70,229,0.06)', color: '#4f46e5', fontWeight: '600' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(79,70,229,0.15)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(79,70,229,0.06)'}>
                  🔬 EMIT Research
                </button>
              )}
              {["thornton.carlton68@gmail.com", "thornton_carlton@yahoo.com", "hbaker@agencyofamerica.com", "cjrealfellas@gmail.com", "kbarthor1@aol.com"].includes(JSON.parse(localStorage.getItem('user') || '{"email":""}').email) && (
                <button onClick={() => { setShowMenu(false); window.location.href = '/emit-research?mode=sermon'; }}
                  style={{ ...menuBtnStyle, background: 'rgba(245,158,11,0.08)', color: '#b45309', fontWeight: '600' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(245,158,11,0.18)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(245,158,11,0.08)'}>
                  {'\uD83D\uDCD6'} EMIT Sermon
                </button>
              )}
              {["thornton.carlton68@gmail.com", "thornton_carlton@yahoo.com"].includes(JSON.parse(localStorage.getItem('user') || '{"email":""}').email) && (
                <button onClick={() => { setShowMenu(false); window.location.href = '/mindstreams-studio'; }}
                  style={{ ...menuBtnStyle, background: 'rgba(212,175,55,0.08)', color: '#d4af37', fontWeight: '600' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(212,175,55,0.15)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(212,175,55,0.08)'}>
                  🎬 MindStreams Studio
                </button>
              )}

              {/* ── Change Home App ── */}
              {!partnerTheme.home && (
              <button onClick={() => { setShowMenu(false); window.location.href = '/select-home-app'; }}
                style={{ ...menuBtnStyle, background: 'rgba(79,70,229,0.06)', color: '#4f46e5', fontWeight: '600' }}
                onMouseOver={(e) => e.target.style.background = 'rgba(79,70,229,0.15)'} onMouseOut={(e) => e.target.style.background = 'rgba(79,70,229,0.06)'}>
                🏠 Change Home App
              </button>
              )}

              <button onClick={() => { setShowMenu(false); localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; }}
                style={{ ...menuBtnStyle, borderBottom: 'none', color: '#dc3545' }}
                onMouseOver={(e) => e.target.style.background = '#fff5f5'} onMouseOut={(e) => e.target.style.background = 'white'}>
                🚪 Logout
              </button>
              <DeleteAccountButton variant="light" onOpen={() => setShowMenu(false)} />
              {partnerTheme.builtBy && (<div style={{ padding: '10px 12px', fontSize: '13px', color: '#5A6A7A', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>Built by MindStreams Technology</div>)}

              {/* FHO Apps Section */}
              {!partnerTheme.home && (
              <div style={{ borderTop: '2px solid #e0e0e0', background: 'linear-gradient(135deg, #E8EEF2, #F0F4F8)', paddingTop: '4px' }}>
                <div style={{ padding: '5px 14px', fontSize: '11px', color: '#666', fontWeight: '600' }}>📱 FHO APPS</div>
                <button onClick={() => { setShowMenu(false); window.location.href = '/family-coach'; }}
                  style={{ width: '100%', padding: '6px 12px 6px 20px', background: 'transparent', border: 'none', color: '#5A6A7A', fontSize: '13px', fontWeight: '500', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s ease' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.5)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
                  👨‍👩‍👧 Family Coach
                </button>
                <button onClick={() => { setShowMenu(false); window.location.href = '/mental-performance'; }}
                  style={{ width: '100%', padding: '6px 12px 6px 20px', background: 'transparent', border: 'none', color: '#5A6A7A', fontSize: '13px', fontWeight: '500', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s ease' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.5)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
                  🏆 Mental Performance
                </button>
                <button id="safe-buddy-btn" onClick={() => { setShowMenu(false); window.location.href = '/safe'; }}
                  style={{ width: '100%', padding: '6px 12px 6px 20px', background: 'transparent', border: 'none', color: '#5A6A7A', fontSize: '13px', fontWeight: '500', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s ease' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.5)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
                  🛡️ SAFE Buddy
                </button>
                <button onClick={() => { setShowMenu(false); window.location.href = '/journey'; }}
                  style={{ width: '100%', padding: '6px 12px 6px 20px', background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(102,126,234,0.15))', border: 'none', borderTop: '1px solid rgba(16,185,129,0.3)', color: '#059669', fontSize: '13px', fontWeight: '600', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s ease' }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(16,185,129,0.25)'} onMouseOut={(e) => e.target.style.background = 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(102,126,234,0.15))'}>
                  ✨ My Journey
                </button>
              </div>
              )}
            </div>
          )}
        </div>
        <div className="subtitle">{activeCoach === 'emit' ? 'Biblical wisdom, instantly applied' : 'Source wisdom, personally guided'}</div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '200px' }}>
        {activeTab === 'progress' && <ProgressTab loading={loading} error={error} userData={userData} onRetry={fetchDashboardData} onPracticeClick={handlePracticeClick} getMindStreamIcon={getMindStreamIcon} getMindStreamColor={getMindStreamColor} activeCoach={activeCoach} />}
        {activeTab === 'analytics' && <AnalyticsTab loading={loading} userData={userData} getLevelInfo={getLevelInfo} getNextLevelUnlock={getNextLevelUnlock} getMindStreamColor={getMindStreamColor} activeCoach={activeCoach} />}
        {activeTab === 'chat' && <ChatTab enableListen={emitVoiceOk} onOpenVoice={emitVoiceOk ? () => setShowVoicePanel(true) : undefined} userName={userName} conversations={conversations} currentConversationId={currentConversationId} chatError={chatError} userInput={userInput} setUserInput={setUserInput} handleSubmit={handleSubmit} handleKeyPress={handleKeyPress} dxiReward={dxiReward} isProcessing={isProcessing} voice={voice} setShowShareModal={setShowShareModal} setShareMessage={setShareMessage} getMindStreamColor={getMindStreamColor} activeCoach={activeCoach} />}
        {activeTab === 'practice' && <PracticeTab practiceForm={practiceForm} setPracticeForm={setPracticeForm} practiceError={practiceError} practiceSuccess={practiceSuccess} isLoggingPractice={isSubmittingPractice} onSubmit={handlePracticeSubmit} categories={categories} />}
      </div>

      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} shareMessage={shareMessage} />
      <PracticeDetailModal isOpen={showPracticeModal} onClose={() => setShowPracticeModal(false)} selectedPractice={selectedPractice} getMindStreamColor={getMindStreamColor} />
      <BibleReferenceModal isOpen={showBibleReference} onClose={() => setShowBibleReference(false)} />
      <UserGuideModal isOpen={showUserGuide} onClose={() => setShowUserGuide(false)} />
      <BiblicalTopicsModal isOpen={showBiblicalTopics} onClose={() => setShowBiblicalTopics(false)} onSelectTopic={handleTopicSelect} />

      {showConversationHistory && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>📜 Conversation History</h2>
              <button onClick={() => setShowConversationHistory(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: '16px', maxHeight: '50vh', overflowY: 'auto' }}>
              {loadingHistory ? <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading conversations...</p>
              : emitConversations.length === 0 ? <p style={{ color: '#6b7280', textAlign: 'center' }}>No conversations yet</p>
              : emitConversations.map((conv, idx) => (
                <div key={conv.id || idx} onClick={() => loadConversationMessages(conv.id)}
                  style={{ padding: '16px', marginBottom: '12px', borderRadius: '12px', background: currentConversationId === conv.id ? '#dbeafe' : '#f3f4f6', cursor: 'pointer', border: currentConversationId === conv.id ? '2px solid #3b82f6' : '2px solid transparent', transition: 'all 0.2s ease' }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>{conv.preview ? conv.preview.substring(0, 60) + '...' : 'Conversation'}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{conv.message_count || 0} messages{conv.last_activity && ` • ${new Date(conv.last_activity).toLocaleDateString()}`}</div>
                  <ConversationActions app="emit" conversationId={conv.id} onDeleted={(id) => { setEmitConversations(prev => prev.filter(c => c.id !== id)); if (currentConversationId === id) { setCurrentConversationId(null); setConversations([{ id: Date.now().toString(), messages: [] }]); } }} />
                </div>
              ))}
            </div>
            <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <button onClick={() => { setCurrentConversationId(null); setConversations([{ id: Date.now().toString(), messages: [] }]); setShowConversationHistory(false); }}
                style={{ padding: '12px 24px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                + Start New Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} passwordForm={passwordForm} setPasswordForm={setPasswordForm} passwordError={passwordError} passwordSuccess={passwordSuccess} isChangingPassword={isChangingPassword} onSubmit={handleChangePassword} />

      <div className="emit-tabs">
        <button className={`emit-tab ${activeTab === 'progress' ? 'active' : ''}`} onClick={() => setActiveTab('progress')}><Target size={24} /><span>Progress</span></button>
        <button className={`emit-tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}><BarChart3 size={24} /><span>Analytics</span></button>
        <button className={`emit-tab ${activeTab === 'chat' ? 'active' : ''}`} onClick={() => setActiveTab('chat')}><MessageCircle size={24} /><span>Chat</span></button>
        <button className={`emit-tab ${activeTab === 'practice' ? 'active' : ''}`} onClick={() => setActiveTab('practice')}><BookOpen size={24} /><span>Log Practice</span></button>
        {['thornton.carlton68@gmail.com','thornton_carlton@yahoo.com'].includes(JSON.parse(localStorage.getItem('user') || '{}')?.email) && (
          <button className="emit-tab" onClick={() => window.location.href = '/partner-dashboard'}>
            <span style={{fontSize:'22px', lineHeight:1}}>🤝</span><span>Partners</span>
          </button>
        )}
      </div>

      {subscriptionStatus === 'free' && responsesRemaining !== null && (
        <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 40 }}>
          <ResponseCounter responsesRemaining={responsesRemaining} />
        </div>
      )}

      <VoiceChoiceModal isOpen={showVoicePanel} theme={partnerTheme} voices={voiceList} choice={voiceChoice}
        onClose={() => { stopCoachVoice(); try { window.speechSynthesis.cancel(); } catch (e) { /* no speech */ } setShowVoicePanel(false); }}
        onChoose={(id) => { setVoiceChoice(id); saveCoachVoice(id); voice.setVoiceOn(true); }}
        onPreview={previewCoachVoice} voiceOn={voice.voiceEnabled} onToggleVoice={() => voice.setVoiceOn(!voice.voiceEnabled)} />
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </div>
  );
};

export default MinimalDashboard;
